import { useId, useState, type KeyboardEvent, type PointerEvent } from "react";
import { niceMax } from "./model";

/**
 * 轻量图表（不引依赖）：单系列，颜色用主题 token（--color-accent），深浅色自动跟随。
 * - ColumnChart：按天的柱状图。整张图是一个可聚焦元素，←/→/Home/End 切换柱子，悬停 / 触摸也会选中；
 *   选中项的数值在提示框里显示，同时写进 aria-live 区域给读屏。
 * - BarList：按 agent / 项目 / 类型排行的横向条形。
 * 两者都配有表格视图（由页面提供），颜色不是唯一的信息来源。
 */

export interface Datum {
  key: string;
  /** 横轴 / 行标签（已格式化） */
  label: string;
  value: number;
  /** 数值文本（已格式化） */
  valueText: string;
}

export function ColumnChart({ data, label, axisText, height = 180 }: { data: Datum[]; label: string; axisText(v: number): string; height?: number }) {
  const id = useId();
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const ticks = [max, max / 2, 0];
  const cur = active !== null ? data[active] : undefined;
  const pick = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !data.length) return;
    const i = Math.floor(((e.clientX - r.left) / r.width) * data.length);
    setActive(Math.min(data.length - 1, Math.max(0, i)));
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!data.length) return;
    const last = data.length - 1;
    const next = e.key === "ArrowRight" ? Math.min(last, (active ?? -1) + 1) : e.key === "ArrowLeft" ? Math.max(0, (active ?? data.length) - 1) : e.key === "Home" ? 0 : e.key === "End" ? last : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };
  const mid = Math.floor((data.length - 1) / 2);
  return (
    <figure className="colchart" style={{ ["--chart-h" as string]: `${height}px` }}>
      <div className="colchart__body">
        <div className="colchart__axis" aria-hidden="true">
          {ticks.map((v, i) => (
            <span key={i} className="colchart__tick" style={{ top: `${(i / 2) * 100}%` }}>
              {axisText(v)}
            </span>
          ))}
        </div>
        <div
          className="colchart__plot"
          role="group"
          tabIndex={0}
          aria-label={label}
          aria-describedby={`${id}-live`}
          onPointerMove={pick}
          onPointerDown={pick}
          onPointerLeave={() => setActive(null)}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
          data-testid="usage-chart"
        >
          <div className="colchart__grid" aria-hidden="true">
            <i style={{ top: 0 }} />
            <i style={{ top: "50%" }} />
            <i style={{ top: "100%" }} />
          </div>
          <div className="colchart__bars" aria-hidden="true">
            {data.map((d, i) => (
              <div key={d.key} className={`colchart__col${active === i ? " is-active" : ""}`} data-testid="usage-bar" data-value={d.value}>
                {d.value > 0 ? <span className="colchart__bar" style={{ height: `${(d.value / max) * 100}%` }} /> : null}
              </div>
            ))}
          </div>
          {cur ? (
            <div
              className="chart-tip"
              role="presentation"
              style={{ left: `${Math.min(88, Math.max(12, ((active! + 0.5) / data.length) * 100))}%` }}
              data-testid="chart-tooltip"
            >
              <span className="chart-tip__label">{cur.label}</span>
              <strong className="num">{cur.valueText}</strong>
            </div>
          ) : null}
        </div>
      </div>
      <div className="colchart__x small muted" aria-hidden="true">
        <span>{data[0]?.label}</span>
        {data.length > 2 ? <span>{data[mid]?.label}</span> : null}
        {data.length > 1 ? <span>{data[data.length - 1]?.label}</span> : null}
      </div>
      <span id={`${id}-live`} className="ui-sr-only" aria-live="polite">
        {cur ? `${cur.label}: ${cur.valueText}` : ""}
      </span>
    </figure>
  );
}

export function BarList({ data, label }: { data: Datum[]; label: string }) {
  const max = Math.max(0, ...data.map((d) => d.value)) || 1;
  return (
    <ul className="barlist" aria-label={label} data-testid="usage-barlist">
      {data.map((d) => (
        <li key={d.key} className="barlist__row" data-testid="usage-barrow">
          <div className="barlist__text small">
            <span className="ellipsis">{d.label}</span>
            <strong className="num">{d.valueText}</strong>
          </div>
          <div className="barlist__track" aria-hidden="true">
            <span className="barlist__bar" style={{ width: `${Math.max(d.value > 0 ? 1 : 0, (d.value / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
